import Empty from "../../components/empty";
import Loader from "../../components/loader";
import {Input, Modal, Pagination, Select} from "antd";
import {useContext, useEffect, useState} from "react";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    FilterOutlined,
    SearchOutlined,
    SolutionOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    TeamOutlined
} from "@ant-design/icons";
import {ApiConsumer} from "../api/ApiConsumer";
import {API_URLS, buildFileDownloadUrl} from "../config";
import {ApploadContext} from "../contexts/Appload";
import {TitleContext} from "../contexts/Title";
import {useNotification} from "../hooks/NotificationHook";
import {UserModal} from "./modal";

const QUALIFICATION_OPTIONS = [
    'High School / Matric',
    'Certificate',
    'Diploma',
    "Bachelor's Degree",
    'Bachelor of Commerce (B.Com)',
    'Bachelor of Science (BSc)',
    'Bachelor of Arts (BA)',
    'Honours Degree',
    "Master's Degree",
    'Doctorate (PhD)',
    'Other'
]

const STATUS_OPTIONS = [
    {value: 'PENDING', label: 'Pending'},
    {value: 'SHORTLISTED', label: 'Shortlisted'},
    {value: 'INTERVIEWED', label: 'Interviewed'},
    {value: 'HIRED', label: 'Hired'},
    {value: 'REJECTED', label: 'Rejected'}
]

const DEFAULT_FILTERS = {
    search: '',
    job_id: undefined,
    status: undefined,
    qualification: undefined,
    skill: '',
    hospital_id: undefined,
    location: '',
    min_years_experience: '',
    max_years_experience: '',
    field_of_study: undefined,
}

const EMPTY_DASHBOARD = {
    total_applicants: 0,
    pending: 0,
    shortlisted: 0,
    interviewed: 0,
    hired: 0,
    rejected: 0,
}

export const CandidatesTable = ({page_title, user_type, no_users = 'No Users'}) => {
    const [users, setUsers] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [ordering, setOrdering] = useState('-created');
    const [hospitals, setHospitals] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [fieldOfStudyOptions, setFieldOfStudyOptions] = useState([]);
    const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [visible, setVisible] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [filters, setFilters] = useState({...DEFAULT_FILTERS});

    let {setTitle, setButton} = useContext(TitleContext);
    let {setLoading} = useContext(ApploadContext)
    let {SuccessMsg, ErrorMsg} = useNotification();

    useEffect(() => {
        setTitle(`${page_title}`)
        setButton(null)

        ApiConsumer.get(API_URLS.DATA.HOSPITALS, {
            params: {
                page: 1,
                size: 500
            }
        })
            .then(res => setHospitals(res.data?.data || []))
            .catch(err => console.error(err))
    }, [])

    function search(targetPage = page, activeFilters = filters) {
        setLoaded(false)

        ApiConsumer(API_URLS.APPLICATIONS.CANDIDATES, {
            params: {
                ...activeFilters,
                ordering,
                size: pageSize,
                page: targetPage
            }
        })
            .then(res => {
                setUsers(res.data?.data || [])
                setTotal(res.data?.count ?? 0)
                setDashboard(res.data?.dashboard ?? EMPTY_DASHBOARD)
                setJobs(res.data?.filter_options?.jobs ?? [])
                setFieldOfStudyOptions(res.data?.filter_options?.field_of_study ?? [])
                setTitle(`${page_title} (${res.data?.count ?? 0})`)
                setPage(res.data?.current_page ?? 1)
                setPageSize(res.data?.size ?? 20)
            })
            .catch(err => console.error(err))
            .finally(() => {
                setLoaded(true)
            })
    }

    useEffect(() => {
        search()
    }, [page, ordering])

    function toggleApplicationDateOrder() {
        setOrdering(prevOrdering => prevOrdering === '-created' ? 'created' : '-created')
        setPage(1)
    }

    function clearFilters() {
        const clearedFilters = {...DEFAULT_FILTERS}
        setFilters(clearedFilters)
        setShowAdvancedFilters(false)
        setPage(1)
        search(1, clearedFilters)
    }

    function downloadCvs() {
        if (exporting) return

        setExporting(true)

        ApiConsumer(API_URLS.APPLICATIONS.CANDIDATE_CV_EXPORT, {
            params: {
                ...filters,
                ordering
            },
            responseType: 'blob'
        })
            .then(res => {
                const blobUrl = window.URL.createObjectURL(new Blob([res.data], {type: 'application/zip'}))
                const link = document.createElement('a')

                link.href = blobUrl
                link.download = `ahc-candidates-${new Date().toISOString().slice(0, 10)}.zip`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
            })
            .catch(async err => {
                let message = 'Could not download CVs. Please try again later.'
                const data = err?.response?.data

                if (data instanceof Blob) {
                    try {
                        const parsed = JSON.parse(await data.text())
                        if (parsed?.message) message = parsed.message
                    } catch (parseError) {
                        console.error(parseError)
                    }
                }

                ErrorMsg(message)
            })
            .finally(() => setExporting(false))
    }

    function countActiveFilters() {
        return Object.values(filters).filter(value => value !== '' && value !== undefined && value !== null).length
    }

    function getStatusMeta(status) {
        const value = normalizeStatusValue(status)

        if (value === 'REJECTED') return {label: 'Rejected', className: 'badge bg-danger'}
        if (value === 'SHORTLISTED') return {
            label: 'Shortlisted',
            className: 'badge bg-pps-gold'
        }
        if (value === 'INTERVIEWED') return {label: 'Interviewed', className: 'badge bg-primary'}
        if (value === 'HIRED') return {label: 'Hired', className: 'badge bg-success'}

        return {label: 'Pending', className: 'badge bg-warning text-dark'}
    }

    function normalizeStatusValue(status) {
        const value = (status || '').toUpperCase().replace(/ /g, '_')

        if (value === 'OPEN' || value === 'PENDING') return 'PENDING'
        if (value === 'SHORT_LIST' || value === 'SHORT_LISTED' || value === 'SHORTLISTED') return 'SHORTLISTED'
        if (value === 'INTERVIEWED') return 'INTERVIEWED'
        if (value === 'HIRED') return 'HIRED'
        if (value === 'REJECTED') return 'REJECTED'

        return 'PENDING'
    }

    function splitCsvValues(value) {
        if (!value) return []

        return String(value)
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
    }

    function getCandidateSkills(candidate) {
        const names = splitCsvValues(candidate?.skill_names)
        const proficiencies = splitCsvValues(candidate?.skill_proficiencies)

        return names.map((name, index) => ({
            name,
            proficiency: proficiencies[index] || ''
        }))
    }

    function toTitleCase(value) {
        if (!value) return ''

        return String(value)
            .split(' ')
            .map(word => word ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : '')
            .join(' ')
    }

    function formatApplicationDate(value) {
        if (!value) return 'Not captured'

        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return 'Not captured'

        return date.toLocaleDateString('en-ZA', {day: '2-digit', month: 'short', year: 'numeric'})
    }

    function updateCandidateStatus(nextStatus) {
        if (!selectedCandidate?.id || normalizeStatusValue(selectedCandidate.status) === nextStatus) {
            return
        }

        setLoading(true)

        ApiConsumer.put(API_URLS.APPLICATIONS.DETAIL(selectedCandidate.id), {status: nextStatus})
            .then(res => {
                const updatedCandidate = res.data

                setSelectedCandidate(updatedCandidate)
                setUsers(prevState => prevState.map(user => user.id === updatedCandidate.id ? {
                    ...user,
                    ...updatedCandidate
                } : user))
                SuccessMsg('Candidate status updated.')
                search(page, filters)
            })
            .catch(() => ErrorMsg('Could not update candidate status. Please try again later.'))
            .finally(() => setLoading(false))
    }

    const dashboardCards = [
        {label: 'Total Applicants', value: dashboard.total_applicants, icon: <TeamOutlined/>, color: '#1f2937'},
        {label: 'Pending', value: dashboard.pending, icon: <ClockCircleOutlined/>, color: '#d97706'},
        {label: 'Shortlisted', value: dashboard.shortlisted, icon: <SolutionOutlined/>, color: '#2563eb'},
        {label: 'Interviewed', value: dashboard.interviewed, icon: <FilterOutlined/>, color: '#7c3aed'},
        {label: 'Hired', value: dashboard.hired, icon: <CheckCircleOutlined/>, color: '#16a34a'},
        {label: 'Rejected', value: dashboard.rejected, icon: <CloseCircleOutlined/>, color: '#dc2626'},
    ]

    const startEntry = total === 0 ? 0 : ((page - 1) * pageSize) + 1
    const endEntry = Math.min(page * pageSize, total)

    return <div>
        <UserModal setVisible={setVisible} visible={visible} userType={user_type}/>

        <Modal
            open={!!selectedCandidate}
            onCancel={() => setSelectedCandidate(null)}
            footer={null}
            centered
            width={720}
            destroyOnClose
            title={null}
            bodyStyle={{padding: 0, borderRadius: 60, overflow: 'hidden'}}
        >
            {selectedCandidate ? (() => {
                const BRAND = '#2563eb'

                const skills = getCandidateSkills(selectedCandidate)
                const normalizedStatus = normalizeStatusValue(selectedCandidate.status)
                const statusMeta = getStatusMeta(selectedCandidate.status)

                const statusPalette = {
                    PENDING: {bg: '#f8fafc', text: '#475569', ring: '#e2e8f0', dot: '#94a3b8'},
                    SHORTLISTED: {bg: '#eff6ff', text: '#1d4ed8', ring: '#bfdbfe', dot: '#2563eb'},
                    INTERVIEWED: {bg: '#f5f3ff', text: '#6d28d9', ring: '#ddd6fe', dot: '#7c3aed'},
                    REJECTED: {bg: '#fef2f2', text: '#b91c1c', ring: '#fecaca', dot: '#ef4444'},
                    HIRED: {bg: '#ecfdf5', text: '#047857', ring: '#a7f3d0', dot: '#10b981'},
                }
                const pal = statusPalette[normalizedStatus] || statusPalette.PENDING

                const chip = {
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999,
                    padding: '5px 12px', fontSize: 12, fontWeight: 500, color: '#475569',
                    textDecoration: 'none',
                }
                const sectionLabel = {fontSize: 13, fontWeight: 600, color: '#0f172a'}
                const marker = {
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${BRAND}12`, color: BRAND,
                }
                const empty = {
                    border: '1px dashed #e2e8f0', background: 'rgba(248,250,252,0.5)',
                    borderRadius: 12, padding: '22px 16px', textAlign: 'center',
                    fontSize: 14, color: '#94a3b8',
                }

                const IconMail = (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="m22 7-10 5L2 7"/>
                    </svg>
                )
                const IconPhone = (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path
                            d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.6 2.6.7a2 2 0 0 1 1.7 2z"/>
                    </svg>
                )
                const IconCap = (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 10 12 5 2 10l10 5 10-5Z"/>
                        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                        <path d="M22 10v6"/>
                    </svg>
                )
                const IconCase = (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2"/>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                )

                const stats = [
                    {
                        label: 'Experience',
                        value: (selectedCandidate.years_of_experience !== null && selectedCandidate.years_of_experience !== undefined)
                            ? `${selectedCandidate.years_of_experience} yrs` : '—',
                    },
                    {label: 'Qualification', value: selectedCandidate.highest_qualification || '—'},
                    {label: 'Skills', value: `${skills.length}`},
                ]

                const hasEducation = selectedCandidate.highest_qualification || selectedCandidate.institution || selectedCandidate.field_of_study
                const hasExperience = selectedCandidate.position || selectedCandidate.company

                return (
                    <div>
                        {/* Hero — the candidate is the header */}
                        <div style={{
                            padding: '32px 28px 24px',
                            background: `linear-gradient(180deg, ${BRAND}0F 0%, #ffffff 100%)`
                        }}>
                            <div style={{display: 'flex', alignItems: 'flex-start', gap: 16}}>
                                <div style={{minWidth: 0, flex: 1}}>
                                    <div style={{
                                        fontSize: 24,
                                        fontWeight: 600,
                                        letterSpacing: '-0.02em',
                                        color: '#0f172a'
                                    }}>
                                        {selectedCandidate.first_name} {selectedCandidate.last_name}
                                    </div>
                                    <div style={{marginTop: 2, fontSize: 14, color: '#64748b'}}>
                                        {selectedCandidate?.job?.hospital?.name || 'Africa Health Care'}
                                    </div>

                                    <div style={{marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                        <a href={`mailto:${selectedCandidate.email}`}
                                           style={{...chip, color: '#475569'}}>
                                            <span style={{color: BRAND, display: 'inline-flex'}}>{IconMail}</span>
                                            {selectedCandidate.email}
                                        </a>
                                        {selectedCandidate.contact_number && (
                                            <a href={`tel:${selectedCandidate.contact_number}`}
                                               style={{...chip, color: '#475569'}}>
                                                <span style={{color: BRAND, display: 'inline-flex'}}>{IconPhone}</span>
                                                {selectedCandidate.contact_number}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Status pill */}
                            <div style={{
                                marginTop: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                flexWrap: 'wrap'
                            }}>
                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <span style={{fontSize: 12, fontWeight: 500, color: '#94a3b8'}}>Status</span>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        borderRadius: 999, padding: '6px 14px',
                                        background: pal.bg, boxShadow: `inset 0 0 0 1px ${pal.ring}`,
                                        fontSize: 13, fontWeight: 600, color: pal.text,
                                    }}>
                                        <span style={{width: 8, height: 8, borderRadius: '50%', background: pal.dot}}/>
                                        {statusMeta.label}
                                    </span>
                                </div>
                                <div style={{minWidth: 220, flex: '0 1 240px'}}>
                                    <Select
                                        className="w-100"
                                        value={normalizedStatus}
                                        onChange={updateCandidateStatus}
                                    >
                                        {STATUS_OPTIONS.map(option => (
                                            <Select.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Stat strip */}
                        <div style={{
                            display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
                            gap: 1, background: '#f1f5f9',
                            borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9',
                        }}>
                            {stats.map((st) => (
                                <div key={st.label} style={{background: '#fff', padding: '16px 20px'}}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 500, letterSpacing: '0.04em',
                                        textTransform: 'uppercase', color: '#94a3b8',
                                    }}>{st.label}</div>
                                    <div style={{
                                        marginTop: 4, fontSize: 14, fontWeight: 600, color: '#0f172a',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>{st.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Body */}
                        <div style={{padding: '28px', display: 'flex', flexDirection: 'column', gap: 28}}>
                            {/* Education */}
                            <div>
                                <div style={{...sectionLabel, marginBottom: 16}}>Education</div>
                                {hasEducation ? (
                                    <div style={{display: 'flex', gap: 14}}>
                                        <div style={marker}>{IconCap}</div>
                                        <div style={{minWidth: 0, flex: 1}}>
                                            <div style={{fontSize: 14, fontWeight: 500, color: '#0f172a'}}>
                                                {selectedCandidate.highest_qualification || 'Qualification not captured'}
                                                {selectedCandidate.field_of_study ? ` in ${selectedCandidate.field_of_study}` : ''}
                                            </div>
                                            {selectedCandidate.institution && (
                                                <div style={{
                                                    fontSize: 14,
                                                    color: '#64748b'
                                                }}>{selectedCandidate.institution}</div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={empty}>No education history listed.</div>
                                )}
                            </div>

                            {/* Experience */}
                            <div>
                                <div style={{...sectionLabel, marginBottom: 16}}>Experience</div>
                                {hasExperience ? (
                                    <div style={{display: 'flex', gap: 14}}>
                                        <div style={marker}>{IconCase}</div>
                                        <div style={{minWidth: 0, flex: 1}}>
                                            <div style={{fontSize: 14, fontWeight: 500, color: '#0f172a'}}>
                                                {selectedCandidate.position || 'Role not captured'}
                                                {selectedCandidate.company && (
                                                    <span style={{
                                                        fontWeight: 400,
                                                        color: '#64748b'
                                                    }}> at {selectedCandidate.company}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={empty}>No work experience listed.</div>
                                )}
                            </div>

                            {/* Skills */}
                            <div>
                                <div style={{
                                    ...sectionLabel,
                                    marginBottom: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    <span>Skills</span>
                                    {skills.length > 0 && (
                                        <span style={{
                                            borderRadius: 999, background: '#f1f5f9', color: '#64748b',
                                            fontSize: 12, fontWeight: 500, padding: '1px 8px',
                                        }}>{skills.length}</span>
                                    )}
                                </div>
                                {skills.length > 0 ? (
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                        {skills.map((skill, index) => (
                                            <span
                                                key={`${skill.name}-${index}`}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    background: '#f8fafc', border: '1px solid #e2e8f0',
                                                    borderRadius: 8, padding: '6px 12px',
                                                    fontSize: 14, color: '#334155',
                                                }}
                                            >
                                        {skill.name}
                                                {skill.proficiency && (
                                                    <span style={{
                                                        fontSize: 12,
                                                        color: '#94a3b8'
                                                    }}>{toTitleCase(skill.proficiency)}</span>
                                                )}
                                    </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={empty}>No skills listed.</div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            borderTop: '1px solid #f1f5f9', background: 'rgba(248,250,252,0.6)',
                            padding: '18px 28px', fontSize: 12, lineHeight: 1.6, color: '#94a3b8',
                        }}>
                            <strong style={{fontWeight: 600, color: '#64748b'}}>
                                {selectedCandidate?.job?.hospital?.name || 'Africa Health Care'}
                            </strong> is an equal opportunity employer. Only shortlisted candidates will be contacted.
                        </div>
                    </div>
                )
            })() : null}
        </Modal>

        <div className="row mb-3">
            {dashboardCards.map(card => (
                <div className="col-xl-2 col-lg-4 col-md-6 mb-3" key={card.label}>
                    <div className="card mb-0">
                        <div className="card-body py-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="text-muted small">{card.icon}<span className="ml-2">{card.label}</span>
                                </div>
                                <h3 className="mb-0" style={{color: card.color}}>{card.value}</h3>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>

        <div className="card mb-3">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                    <div className="d-flex align-items-center">
                        <h6 className="mb-0"><FilterOutlined className="mr-2"/>Filters</h6>
                        <span className="badge badge-pill bg-primary ml-2">{countActiveFilters()}</span>
                    </div>
                    <button className="btn btn-link p-0" onClick={clearFilters}>Clear all</button>
                </div>

                <div className="mb-3">
                    <Input
                        prefix={<SearchOutlined/>}
                        placeholder="Search by applicant name or email..."
                        value={filters.search}
                        onChange={event => setFilters(prevState => ({
                            ...prevState,
                            search: event.target.value
                        }))}
                    />
                </div>

                <div className="row">
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="small text-muted mb-1">Job Position</label>
                        <Select
                            allowClear
                            className="w-100"
                            placeholder="All job positions"
                            value={filters.job_id}
                            onChange={value => setFilters(prevState => ({
                                ...prevState,
                                job_id: value
                            }))}
                        >
                            {jobs.map(job => (
                                <Select.Option key={job.id} value={job.id}>
                                    {job.title}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="small text-muted mb-1">Status</label>
                        <Select
                            allowClear
                            className="w-100"
                            placeholder="All statuses"
                            value={filters.status}
                            onChange={value => setFilters(prevState => ({
                                ...prevState,
                                status: value
                            }))}
                        >
                            {STATUS_OPTIONS.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="small text-muted mb-1">Qualification / Degree</label>
                        <Select
                            allowClear
                            className="w-100"
                            placeholder="Select qualification..."
                            value={filters.qualification}
                            onChange={value => setFilters(prevState => ({
                                ...prevState,
                                qualification: value
                            }))}
                        >
                            {QUALIFICATION_OPTIONS.map(option => (
                                <Select.Option key={option} value={option}>
                                    {option}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="small text-muted mb-1">Skill</label>
                        <Input
                            placeholder="e.g. ICU, Nursing, SAP"
                            value={filters.skill}
                            onChange={event => setFilters(prevState => ({
                                ...prevState,
                                skill: event.target.value
                            }))}
                        />
                    </div>
                </div>

                <button className="btn btn-link p-0 mb-3"
                        onClick={() => setShowAdvancedFilters(prevState => !prevState)}>
                    {showAdvancedFilters ? 'Hide advanced filters' : 'Advanced filters'}
                </button>

                {showAdvancedFilters ? <div className="row">
                    <div className="col-lg-2 col-md-4 mb-3">
                        <label className="small text-muted mb-1">Hospital</label>
                        <Select
                            allowClear
                            className="w-100"
                            placeholder="All hospitals"
                            value={filters.hospital_id}
                            onChange={value => setFilters(prevState => ({
                                ...prevState,
                                hospital_id: value
                            }))}
                        >
                            {hospitals.map(hospital => (
                                <Select.Option key={hospital.id} value={hospital.id}>
                                    {hospital.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="col-lg-2 col-md-4 mb-3">
                        <label className="small text-muted mb-1">Location</label>
                        <Input
                            placeholder="e.g. Johannesburg"
                            value={filters.location}
                            onChange={event => setFilters(prevState => ({
                                ...prevState,
                                location: event.target.value
                            }))}
                        />
                    </div>
                    <div className="col-lg-2 col-md-4 mb-3">
                        <label className="small text-muted mb-1">Min Years Experience</label>
                        <Input
                            type="number"
                            placeholder="e.g. 5"
                            value={filters.min_years_experience}
                            onChange={event => setFilters(prevState => ({
                                ...prevState,
                                min_years_experience: event.target.value
                            }))}
                        />
                    </div>
                    <div className="col-lg-2 col-md-4 mb-3">
                        <label className="small text-muted mb-1">Max Years Experience</label>
                        <Input
                            type="number"
                            placeholder="e.g. 10"
                            value={filters.max_years_experience}
                            onChange={event => setFilters(prevState => ({
                                ...prevState,
                                max_years_experience: event.target.value
                            }))}
                        />
                    </div>
                    <div className="col-lg-4 col-md-8 mb-3">
                        <label className="small text-muted mb-1">Field of Study</label>
                        <Select
                            allowClear
                            className="w-100"
                            placeholder="All fields"
                            value={filters.field_of_study}
                            onChange={value => setFilters(prevState => ({
                                ...prevState,
                                field_of_study: value
                            }))}
                        >
                            {fieldOfStudyOptions.map(option => (
                                <Select.Option key={option} value={option}>
                                    {option}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div> : null}

                <div className="d-flex justify-content-end align-items-center">
                    <button className="btn rounded-round btn-lg legitRipple btn-outline-secondary mr-2"
                            disabled={exporting}
                            onClick={downloadCvs}>
                        <DownloadOutlined className="mr-1"/>{exporting ? 'Preparing...' : 'Download CVs'}
                    </button>
                    <button className="btn btn-sm rounded-round bg-pps-blue btn-lg legitRipple"
                            onClick={() => {
                                setPage(1)
                                search(1)
                            }}>
                        Search
                    </button>
                </div>
            </div>
        </div>

        <div className="card">
            <table className="table datatable-hospitals table-hover">
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Surname</th>
                    <th>Email</th>
                    <th>Hospital</th>
                    <th>Location</th>
                    <th>Job</th>
                    <th>Qualification</th>
                    <th>Experience</th>
                    <th>Status</th>
                    <th onClick={toggleApplicationDateOrder}
                        style={{cursor: 'pointer', whiteSpace: 'nowrap'}}>
                        Applied
                        <span className="ml-1">
                            {ordering === '-created' ? <SortDescendingOutlined/> : <SortAscendingOutlined/>}
                        </span>
                    </th>
                    <th>CV</th>
                    <th>Cover Letter</th>
                </tr>
                </thead>
                <tbody>
                {
                    loaded ?
                        users.length === 0 ?
                            <tr>
                                <td colSpan={12}><Empty description={no_users}/></td>
                            </tr> :
                            users.map((user, index) => {
                                const statusMeta = getStatusMeta(user.status)

                                return <tr
                                    key={index}
                                    onClick={() => setSelectedCandidate(user)}
                                    style={{cursor: 'pointer'}}
                                >
                                    <td>{user.first_name}</td>
                                    <td>{user.last_name}</td>
                                    <td>{user.email}</td>
                                    <td>{user?.job?.hospital?.name}</td>
                                    <td>{user.candidate_location || user?.job?.location || 'Not captured'}</td>
                                    <td>{user?.job?.title}</td>
                                    <td>{user.highest_qualification || 'Not captured'}</td>
                                    <td>{user.years_of_experience !== null && user.years_of_experience !== undefined ? `${user.years_of_experience} years` : 'Not captured'}</td>
                                    <td><span className={statusMeta.className}>{statusMeta.label}</span></td>
                                    <td style={{whiteSpace: 'nowrap'}}>{formatApplicationDate(user.created)}</td>
                                    <td><a href={buildFileDownloadUrl(user.cv_file_url)}
                                           onClick={event => event.stopPropagation()}
                                           target='_blank'
                                           rel="noreferrer"
                                           className="text-center">Download CV</a></td>
                                    <td>
                                        {user.cover_letter_file_url ?
                                            <a href={buildFileDownloadUrl(user.cover_letter_file_url)}
                                               onClick={event => event.stopPropagation()}
                                               target='_blank'
                                               rel="noreferrer"
                                               className="text-center">Download Cover Letter</a> :
                                            <p className="mb-0">No cover letter</p>
                                        }
                                    </td>
                                </tr>
                            }) :
                        <tr>
                            <td colSpan={12}><Loader/></td>
                        </tr>
                }
                </tbody>
            </table>
            <div className="datatable-footer">
                <div className="dataTables_info" id="DataTables_Table_0_info" role="status"
                     aria-live="polite">
                    Showing {startEntry} to {endEntry} of {total} entries
                </div>
                <div className="dataTables_paginate paging_simple_numbers"
                     id="DataTables_Table_0_paginate">
                    <Pagination className="pagination shadow-1 text-center"
                                total={total}
                                current={page}
                                pageSize={pageSize}
                                onChange={selected => {
                                    setPage(selected)
                                }}
                    />
                </div>
            </div>
        </div>
    </div>
}
